// §11.8 — the PDF object layer: the lexer, the object model, and the stream filters.
//
// **Why this exists rather than a dependency** (D-170): §22's allowed runtime deps name no PDF
// library, and the one alternative — handing the file to the browser's own viewer — was measured
// against §19.1's exact CSP and found to be blocked outright: `object-src` falls back to
// `default-src 'self'`, which does not match a `blob:`, and `frame-src https:` refuses both a
// `blob:` and a same-origin `http:` document. A PDF read out of OPFS is a blob, so there is
// nothing to hand it to. That leaves §11.2's own answer for the Terminal — "own renderer, no
// dependency" — applied to the one other format §11 asks Hangar to display.
//
// The scope is deliberate and stated in one place: this reads a document's **text**, which is what
// §11.8's four features need (paged view, zoom, text search, download). It does not rasterise
// vector graphics or images, and a page with no text says so rather than pretending.
import { inflateSync, unzlibSync } from 'fflate';

export interface PdfName {
  readonly kind: 'name';
  readonly name: string;
}
export interface PdfRef {
  readonly kind: 'ref';
  readonly num: number;
  readonly gen: number;
}
export interface PdfStr {
  readonly kind: 'str';
  readonly bytes: Uint8Array;
}
export interface PdfArray {
  readonly kind: 'array';
  readonly items: readonly PdfValue[];
}
export interface PdfDict {
  readonly kind: 'dict';
  readonly entries: ReadonlyMap<string, PdfValue>;
}
export interface PdfStream {
  readonly kind: 'stream';
  readonly dict: PdfDict;
  /** Still filtered — `decodeStream` is what turns it into bytes. */
  readonly raw: Uint8Array;
}
/** A bare operator keyword inside a content stream (`Tj`, `re`, `BT`…). Never a file-level value. */
export interface PdfOp {
  readonly kind: 'op';
  readonly op: string;
}

export type PdfValue =
  | number
  | boolean
  | null
  | PdfName
  | PdfRef
  | PdfStr
  | PdfArray
  | PdfDict
  | PdfStream
  | PdfOp;

export const name = (value: string): PdfName => ({ kind: 'name', name: value });

export function isDict(value: PdfValue | undefined): value is PdfDict {
  return typeof value === 'object' && value !== null && value.kind === 'dict';
}
export function isStream(value: PdfValue | undefined): value is PdfStream {
  return typeof value === 'object' && value !== null && value.kind === 'stream';
}
export function isArray(value: PdfValue | undefined): value is PdfArray {
  return typeof value === 'object' && value !== null && value.kind === 'array';
}
export function isName(value: PdfValue | undefined): value is PdfName {
  return typeof value === 'object' && value !== null && value.kind === 'name';
}
export function isRef(value: PdfValue | undefined): value is PdfRef {
  return typeof value === 'object' && value !== null && value.kind === 'ref';
}
export function isStr(value: PdfValue | undefined): value is PdfStr {
  return typeof value === 'object' && value !== null && value.kind === 'str';
}

/** A stream's own dictionary answers `get` too — `/Length` and `/Filter` live there. */
export function dictOf(value: PdfValue | undefined): PdfDict | undefined {
  if (isDict(value)) return value;
  if (isStream(value)) return value.dict;
  return undefined;
}

export function get(value: PdfValue | undefined, key: string): PdfValue | undefined {
  return dictOf(value)?.entries.get(key);
}

const SPACE = new Set([0x00, 0x09, 0x0a, 0x0c, 0x0d, 0x20]);
const DELIM = new Set([0x28, 0x29, 0x3c, 0x3e, 0x5b, 0x5d, 0x7b, 0x7d, 0x2f, 0x25]);

const at = (bytes: Uint8Array, index: number): number => bytes[index] ?? -1;
const isSpace = (byte: number): boolean => SPACE.has(byte);
const isRegular = (byte: number): boolean => byte >= 0 && !SPACE.has(byte) && !DELIM.has(byte);

/** Latin-1, which is what a PDF name or keyword is. Never used for document text. */
export function latin1(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) out += String.fromCharCode(byte);
  return out;
}

export function bytesOf(text: string): Uint8Array {
  const out = new Uint8Array(text.length);
  for (let index = 0; index < text.length; index += 1) out[index] = text.charCodeAt(index) & 0xff;
  return out;
}

/**
 * The lexer. One instance walks one buffer — a file, or a decoded content stream.
 *
 * It is deliberately forgiving: a PDF in the wild is frequently a PDF plus whatever the tool that
 * produced it did wrong, and a reader that throws on the first surprise reads almost nothing.
 * Every recovery here is a *local* one — an unparseable token is skipped, never guessed at.
 */
export class PdfLexer {
  pos = 0;

  constructor(readonly bytes: Uint8Array) {}

  get done(): boolean {
    return this.pos >= this.bytes.length;
  }

  skipSpace(): void {
    while (this.pos < this.bytes.length) {
      const byte = at(this.bytes, this.pos);
      if (isSpace(byte)) {
        this.pos += 1;
      } else if (byte === 0x25) {
        // A comment runs to the end of the line.
        while (this.pos < this.bytes.length && at(this.bytes, this.pos) !== 0x0a) this.pos += 1;
      } else {
        return;
      }
    }
  }

  /** The next raw token as text — used for keywords (`obj`, `stream`, `R`). */
  peekKeyword(): string {
    const save = this.pos;
    this.skipSpace();
    const start = this.pos;
    while (this.pos < this.bytes.length && isRegular(at(this.bytes, this.pos))) this.pos += 1;
    const word = latin1(this.bytes.subarray(start, this.pos));
    this.pos = save;
    return word;
  }

  private readRegular(): string {
    const start = this.pos;
    while (this.pos < this.bytes.length && isRegular(at(this.bytes, this.pos))) this.pos += 1;
    return latin1(this.bytes.subarray(start, this.pos));
  }

  private readName(): PdfName {
    this.pos += 1; // '/'
    let out = '';
    while (this.pos < this.bytes.length && isRegular(at(this.bytes, this.pos))) {
      const byte = at(this.bytes, this.pos);
      if (byte === 0x23 && this.pos + 2 < this.bytes.length) {
        const hex = latin1(this.bytes.subarray(this.pos + 1, this.pos + 3));
        const code = Number.parseInt(hex, 16);
        if (Number.isFinite(code)) {
          out += String.fromCharCode(code);
          this.pos += 3;
          continue;
        }
      }
      out += String.fromCharCode(byte);
      this.pos += 1;
    }
    return name(out);
  }

  private readLiteralString(): PdfStr {
    this.pos += 1; // '('
    const out: number[] = [];
    let depth = 1;
    while (this.pos < this.bytes.length) {
      const byte = at(this.bytes, this.pos);
      this.pos += 1;
      if (byte === 0x5c) {
        const escape = at(this.bytes, this.pos);
        this.pos += 1;
        if (escape === 0x6e) out.push(0x0a);
        else if (escape === 0x72) out.push(0x0d);
        else if (escape === 0x74) out.push(0x09);
        else if (escape === 0x62) out.push(0x08);
        else if (escape === 0x66) out.push(0x0c);
        else if (escape >= 0x30 && escape <= 0x37) {
          // Up to three octal digits.
          let code = escape - 0x30;
          for (let step = 0; step < 2; step += 1) {
            const next = at(this.bytes, this.pos);
            if (next < 0x30 || next > 0x37) break;
            code = code * 8 + (next - 0x30);
            this.pos += 1;
          }
          out.push(code & 0xff);
        } else if (escape === 0x0a) {
          // A backslash before a newline is a line continuation: nothing is added.
        } else if (escape === 0x0d) {
          if (at(this.bytes, this.pos) === 0x0a) this.pos += 1;
        } else {
          out.push(escape);
        }
        continue;
      }
      if (byte === 0x28) depth += 1;
      if (byte === 0x29) {
        depth -= 1;
        if (depth === 0) break;
      }
      out.push(byte);
    }
    return { kind: 'str', bytes: Uint8Array.from(out) };
  }

  private readHexString(): PdfStr {
    this.pos += 1; // '<'
    const digits: number[] = [];
    while (this.pos < this.bytes.length) {
      const byte = at(this.bytes, this.pos);
      this.pos += 1;
      if (byte === 0x3e) break;
      const digit = hexDigit(byte);
      if (digit >= 0) digits.push(digit);
    }
    // An odd number of digits means the last one is padded with a zero.
    if (digits.length % 2 === 1) digits.push(0);
    const out = new Uint8Array(digits.length / 2);
    for (let index = 0; index < out.length; index += 1) {
      out[index] = ((digits[index * 2] ?? 0) << 4) | (digits[index * 2 + 1] ?? 0);
    }
    return { kind: 'str', bytes: out };
  }

  /**
   * One value. `resolveLength` is how a stream learns its own `/Length` when that is an indirect
   * reference — the document supplies it once the object map exists.
   */
  next(resolveLength?: (ref: PdfRef) => number | undefined): PdfValue | undefined {
    this.skipSpace();
    if (this.done) return undefined;
    const byte = at(this.bytes, this.pos);

    if (byte === 0x2f) return this.readName();
    if (byte === 0x28) return this.readLiteralString();
    if (byte === 0x3c) {
      if (at(this.bytes, this.pos + 1) === 0x3c) return this.readDictOrStream(resolveLength);
      return this.readHexString();
    }
    if (byte === 0x5b) {
      this.pos += 1;
      const items: PdfValue[] = [];
      for (;;) {
        this.skipSpace();
        if (this.done) break;
        if (at(this.bytes, this.pos) === 0x5d) {
          this.pos += 1;
          break;
        }
        const item = this.next(resolveLength);
        if (item === undefined) break;
        items.push(item);
      }
      return { kind: 'array', items };
    }
    if (byte === 0x5d || byte === 0x3e || byte === 0x29 || byte === 0x7b || byte === 0x7d) {
      // A stray closer. Consume it so the caller cannot loop forever on it.
      this.pos += 1;
      return { kind: 'op', op: String.fromCharCode(byte) };
    }

    const word = this.readRegular();
    if (word.length === 0) {
      this.pos += 1;
      return { kind: 'op', op: '' };
    }
    if (word === 'true') return true;
    if (word === 'false') return false;
    if (word === 'null') return null;

    if (/^[+-]?(\d+\.?\d*|\.\d+)$/.test(word)) {
      const value = Number.parseFloat(word);
      // `<num> <gen> R` — an indirect reference, which only looks like two numbers until the `R`.
      if (Number.isInteger(value) && value >= 0) {
        const save = this.pos;
        this.skipSpace();
        const genWord = this.readRegular();
        if (/^\d+$/.test(genWord)) {
          this.skipSpace();
          if (this.readRegular() === 'R') {
            return { kind: 'ref', num: value, gen: Number.parseInt(genWord, 10) };
          }
        }
        this.pos = save;
      }
      return value;
    }

    return { kind: 'op', op: word };
  }

  private readDictOrStream(resolveLength?: (ref: PdfRef) => number | undefined): PdfValue {
    this.pos += 2; // '<<'
    const entries = new Map<string, PdfValue>();
    for (;;) {
      this.skipSpace();
      if (this.done) break;
      if (at(this.bytes, this.pos) === 0x3e && at(this.bytes, this.pos + 1) === 0x3e) {
        this.pos += 2;
        break;
      }
      if (at(this.bytes, this.pos) !== 0x2f) {
        // Not a key. Consume one value so a malformed dictionary cannot spin.
        const skipped = this.next(resolveLength);
        if (skipped === undefined) break;
        continue;
      }
      const key = this.readName();
      const value = this.next(resolveLength);
      if (value === undefined) break;
      entries.set(key.name, value);
    }

    const dict: PdfDict = { kind: 'dict', entries };
    const save = this.pos;
    this.skipSpace();
    if (this.peekKeyword() !== 'stream') {
      this.pos = save;
      return dict;
    }

    this.pos += 'stream'.length;
    // Exactly one CRLF or LF follows the keyword.
    if (at(this.bytes, this.pos) === 0x0d) this.pos += 1;
    if (at(this.bytes, this.pos) === 0x0a) this.pos += 1;
    const start = this.pos;

    const declared = entries.get('Length');
    let length =
      typeof declared === 'number'
        ? declared
        : isRef(declared)
          ? resolveLength?.(declared)
          : undefined;

    // A declared length is only believed if `endstream` is actually where it says. Producers get
    // this wrong often enough that trusting it blindly loses whole objects.
    if (length === undefined || !endstreamFollows(this.bytes, start + length)) {
      length = findEndstream(this.bytes, start) - start;
    }
    const end = Math.max(start, Math.min(this.bytes.length, start + length));
    this.pos = end;
    this.skipSpace();
    if (this.peekKeyword() === 'endstream') this.pos += 'endstream'.length;
    return { kind: 'stream', dict, raw: this.bytes.subarray(start, end) };
  }
}

function hexDigit(byte: number): number {
  if (byte >= 0x30 && byte <= 0x39) return byte - 0x30;
  if (byte >= 0x41 && byte <= 0x46) return byte - 0x41 + 10;
  if (byte >= 0x61 && byte <= 0x66) return byte - 0x61 + 10;
  return -1;
}

const ENDSTREAM = bytesOf('endstream');

function endstreamFollows(bytes: Uint8Array, from: number): boolean {
  if (from < 0 || from > bytes.length) return false;
  // Allow the EOL the spec puts before the keyword.
  let cursor = from;
  for (let step = 0; step < 2 && isSpace(at(bytes, cursor)); step += 1) cursor += 1;
  return matchesAt(bytes, cursor, ENDSTREAM);
}

export function matchesAt(bytes: Uint8Array, from: number, needle: Uint8Array): boolean {
  if (from + needle.length > bytes.length) return false;
  for (let index = 0; index < needle.length; index += 1) {
    if (bytes[from + index] !== needle[index]) return false;
  }
  return true;
}

export function indexOfBytes(bytes: Uint8Array, needle: Uint8Array, from = 0): number {
  const last = bytes.length - needle.length;
  for (let index = Math.max(0, from); index <= last; index += 1) {
    if (matchesAt(bytes, index, needle)) return index;
  }
  return -1;
}

function findEndstream(bytes: Uint8Array, from: number): number {
  const found = indexOfBytes(bytes, ENDSTREAM, from);
  if (found < 0) return bytes.length;
  // Trim the EOL the producer wrote before the keyword; it is not part of the data.
  let end = found;
  if (at(bytes, end - 1) === 0x0a) end -= 1;
  if (at(bytes, end - 1) === 0x0d) end -= 1;
  return end;
}

// --- Filters -----------------------------------------------------------------------------------

/** What a filter this reader does not implement leaves behind, so the UI can say which one. */
export class UnsupportedFilter extends Error {
  constructor(readonly filter: string) {
    super(`unsupported PDF filter: ${filter}`);
    this.name = 'UnsupportedFilter';
  }
}

function inflate(bytes: Uint8Array): Uint8Array {
  // Nearly every PDF writes zlib-wrapped deflate; a few write raw. Trying both is cheaper than
  // guessing from the header, which some producers get wrong too.
  try {
    return unzlibSync(bytes);
  } catch {
    return inflateSync(bytes);
  }
}

function asciiHexDecode(bytes: Uint8Array): Uint8Array {
  const digits: number[] = [];
  for (const byte of bytes) {
    if (byte === 0x3e) break;
    const digit = hexDigit(byte);
    if (digit >= 0) digits.push(digit);
  }
  if (digits.length % 2 === 1) digits.push(0);
  const out = new Uint8Array(digits.length / 2);
  for (let index = 0; index < out.length; index += 1) {
    out[index] = ((digits[index * 2] ?? 0) << 4) | (digits[index * 2 + 1] ?? 0);
  }
  return out;
}

function ascii85Decode(bytes: Uint8Array): Uint8Array {
  const out: number[] = [];
  let group: number[] = [];
  for (let index = 0; index < bytes.length; index += 1) {
    const byte = at(bytes, index);
    if (isSpace(byte)) continue;
    if (byte === 0x7e) break; // '~>' terminator
    if (byte === 0x7a && group.length === 0) {
      out.push(0, 0, 0, 0);
      continue;
    }
    if (byte < 0x21 || byte > 0x75) continue;
    group.push(byte - 0x21);
    if (group.length === 5) {
      let value = 0;
      for (const digit of group) value = value * 85 + digit;
      out.push((value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff);
      group = [];
    }
  }
  if (group.length > 1) {
    const partial = group.length;
    while (group.length < 5) group.push(84);
    let value = 0;
    for (const digit of group) value = value * 85 + digit;
    const full = [(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff];
    for (let index = 0; index < partial - 1; index += 1) out.push(full[index] ?? 0);
  }
  return Uint8Array.from(out);
}

function runLengthDecode(bytes: Uint8Array): Uint8Array {
  const out: number[] = [];
  let index = 0;
  while (index < bytes.length) {
    const marker = at(bytes, index);
    index += 1;
    if (marker === 128) break;
    if (marker < 128) {
      for (let step = 0; step <= marker; step += 1) out.push(at(bytes, index + step));
      index += marker + 1;
    } else {
      const byte = at(bytes, index);
      index += 1;
      for (let step = 0; step < 257 - marker; step += 1) out.push(byte);
    }
  }
  return Uint8Array.from(out);
}

/**
 * The PNG predictors, which is how a cross-reference stream and most object streams are written.
 * Without this an xref stream decodes to noise and the document looks like it has no pages.
 */
function undoPredictor(data: Uint8Array, predictor: number, colors: number, bpc: number, columns: number): Uint8Array {
  if (predictor < 10) return data;
  const bytesPerPixel = Math.max(1, Math.ceil((colors * bpc) / 8));
  const rowLength = Math.ceil((colors * bpc * columns) / 8);
  const rows = Math.floor(data.length / (rowLength + 1));
  const out = new Uint8Array(rows * rowLength);
  let previous = new Uint8Array(rowLength);

  for (let row = 0; row < rows; row += 1) {
    const tag = at(data, row * (rowLength + 1));
    const source = data.subarray(row * (rowLength + 1) + 1, (row + 1) * (rowLength + 1));
    const current = new Uint8Array(rowLength);
    for (let index = 0; index < rowLength; index += 1) {
      const raw = at(source, index);
      const left = index >= bytesPerPixel ? (current[index - bytesPerPixel] ?? 0) : 0;
      const up = previous[index] ?? 0;
      const upLeft = index >= bytesPerPixel ? (previous[index - bytesPerPixel] ?? 0) : 0;
      let value = raw;
      if (tag === 1) value = raw + left;
      else if (tag === 2) value = raw + up;
      else if (tag === 3) value = raw + ((left + up) >> 1);
      else if (tag === 4) {
        const estimate = left + up - upLeft;
        const dLeft = Math.abs(estimate - left);
        const dUp = Math.abs(estimate - up);
        const dUpLeft = Math.abs(estimate - upLeft);
        value = raw + (dLeft <= dUp && dLeft <= dUpLeft ? left : dUp <= dUpLeft ? up : upLeft);
      }
      current[index] = value & 0xff;
    }
    out.set(current, row * rowLength);
    previous = current;
  }
  return out;
}

const numberOr = (value: PdfValue | undefined, fallback: number): number =>
  typeof value === 'number' ? value : fallback;

/**
 * A stream's bytes, filters applied. `resolve` follows indirect references, because `/Filter` and
 * `/DecodeParms` are both allowed to be one.
 */
export function decodeStream(
  stream: PdfStream,
  resolve: (value: PdfValue | undefined) => PdfValue | undefined = (value) => value,
): Uint8Array {
  const filterValue = resolve(stream.dict.entries.get('Filter'));
  const filters = isName(filterValue)
    ? [filterValue]
    : isArray(filterValue)
      ? filterValue.items.map(resolve).filter(isName)
      : [];

  const parmsValue = resolve(stream.dict.entries.get('DecodeParms') ?? stream.dict.entries.get('DP'));
  const parms = isArray(parmsValue) ? parmsValue.items.map(resolve) : [parmsValue];

  let data = stream.raw;
  for (let index = 0; index < filters.length; index += 1) {
    const filter = filters[index]?.name ?? '';
    if (filter === 'FlateDecode' || filter === 'Fl') data = inflate(data);
    else if (filter === 'ASCIIHexDecode' || filter === 'AHx') data = asciiHexDecode(data);
    else if (filter === 'ASCII85Decode' || filter === 'A85') data = ascii85Decode(data);
    else if (filter === 'RunLengthDecode' || filter === 'RL') data = runLengthDecode(data);
    else if (filter === 'Crypt') continue;
    else throw new UnsupportedFilter(filter);

    const parm = dictOf(parms[index] ?? undefined);
    if (parm) {
      const predictor = numberOr(resolve(parm.entries.get('Predictor')), 1);
      if (predictor > 1) {
        data = undoPredictor(
          data,
          predictor,
          numberOr(resolve(parm.entries.get('Colors')), 1),
          numberOr(resolve(parm.entries.get('BitsPerComponent')), 8),
          numberOr(resolve(parm.entries.get('Columns')), 1),
        );
      }
    }
  }
  return data;
}
